import { ChevronDown, ChevronUp, ChevronsUpDown } from "lucide-react";

import { TableHead } from "../../ui/table";
import { cn } from "../../utils/common/cn";

export type SortDir = "asc" | "desc";

/**
 * A column header that only REPORTS the sort the user chose: a server-paged table sends it to the backend for the sorted rows (Manage
 * Capital's Contract Eligibility, Discount & Spread and Zero Out Rules), and a small table that holds every row applies it itself
 * (Capital Sources). SortableTableHead is for the TanStack tables that sort in the browser.
 *
 * Clicking cycles ascending -> descending -> the table's own order. The neutral arrow is always drawn, so a header that can sort looks
 * like one and does not shift when it becomes active.
 */
export function ServerSortHead<K extends string>({
  label,
  sortKey,
  active,
  dir,
  onSort,
  className,
}: {
  label: string;
  sortKey: K;
  /** The column the table is sorted by now, or null for its own order. */
  active: K | null;
  dir: SortDir;
  onSort: (key: K | null, dir: SortDir) => void;
  className?: string;
}) {
  const sorted = active === sortKey ? dir : null;

  function next() {
    if (sorted === null) onSort(sortKey, "asc");
    else if (sorted === "asc") onSort(sortKey, "desc");
    else onSort(null, "asc");
  }

  return (
    <TableHead className={className} aria-sort={sorted === "asc" ? "ascending" : sorted === "desc" ? "descending" : "none"}>
      <button
        type="button"
        onClick={next}
        aria-label={`Sort by ${label}${sorted === "asc" ? ", currently ascending" : sorted === "desc" ? ", currently descending" : ""}`}
        className={cn(
          "-mx-1 inline-flex items-center gap-1 rounded px-1 py-0.5 transition-colors hover:text-foreground",
          sorted ? "text-foreground" : "text-muted-foreground",
        )}
      >
        {label}
        {sorted === "asc" ? (
          <ChevronUp className="h-3 w-3 shrink-0" />
        ) : sorted === "desc" ? (
          <ChevronDown className="h-3 w-3 shrink-0" />
        ) : (
          <ChevronsUpDown className="h-3 w-3 shrink-0 opacity-40" />
        )}
      </button>
    </TableHead>
  );
}
